import { useState, type FormEvent } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { apiClient } from '../../api/client'
import { useAuthStore, type User } from '../../store/authStore'
import { AuthHeroLayout } from '../../components/auth/AuthHeroLayout'
import { PhoneInput } from '../../components/auth/PhoneInput'
import { PasswordInput } from '../../components/auth/PasswordInput'
import { prepareForSubmit, type CountryCode } from '@jnati/phone'
import { FeaturesSection } from '../../components/landing/FeaturesSection'
import { HowItWorksSection } from '../../components/landing/HowItWorksSection'
import { TestimonialsSection } from '../../components/landing/TestimonialsSection'
import { AboutSection } from '../../components/landing/AboutSection'
import { ContactSection } from '../../components/landing/ContactSection'
import { getErrorMessage } from '../../utils/errorMessage'
// #kinship-quiz-cutover-route — login no longer embeds the quiz.
// QuizCallout below is a small card-style CTA linking to /quiz
// where V2 renders standalone. Keeps the login form unobstructed
// while still surfacing the quiz prominently.

// Mirrors apps/mobile/app/(auth)/login.tsx — phone + password →
// POST /api/auth/login → JWT. /login is now the full marketing-
// equivalent landing (#7r2): hero + login card + 5 content sections +
// footer. /signup and /forgot-password stay minimal.

interface LoginResponse {
  token: string
  refreshToken?: string
  userId: number
  firstName: string
  lastName: string
  email?: string
  profilePhoto?: string | null
  preferredLanguage?: string
  gender?: 'male' | 'female' | null
  kinshipLabelPreference?: 'telugu_script' | 'romanized' | 'both'
  isAdmin?: boolean
  eraseRequestedAt?: string | null
}

export function LoginPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const setAuth = useAuthStore((s) => s.setAuth)

  const [phone, setPhone] = useState('')
  // #intl-auth-1c: default India. The selector offers all 7 allowlisted
  // countries; non-India is blocked below with a clean "coming soon" until
  // the Phase 2 migration. India (the default) is byte-identical to pre-1c.
  const [country, setCountry] = useState<CountryCode>('IN')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    if (!phone.trim() || !password) {
      setError('Enter phone number and password')
      return
    }
    // #e164-migration Release C: validate + assemble E.164 for the selected
    // country (all 7 now functional), then send the E.164 value. India →
    // prep.e164 = +91<national> (Deepthi 9177719333 → +919177719333); the
    // backend dual-read matches the stored E.164 row. Invalid → message, no POST.
    const prep = prepareForSubmit(phone, country)
    if (!prep.ok) {
      setError(prep.message)
      return
    }
    setLoading(true)
    setError('')
    try {
      const res = await apiClient.post<LoginResponse>('/api/auth/login', {
        phoneNumber: prep.e164,
        password,
      })
      const data = res.data
      const user: User = {
        id: data.userId,
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email ?? '',
        profilePhoto: data.profilePhoto ?? null,
        preferredLanguage: data.preferredLanguage ?? 'en',
        gender: data.gender ?? null,
        kinshipLabelPreference: data.kinshipLabelPreference ?? 'telugu_script',
        isAdmin: data.isAdmin === true,
        eraseRequestedAt: data.eraseRequestedAt ?? null,
      }
      localStorage.setItem('token', data.token)
      // #auth-refresh-interceptor: apiClient's response interceptor
      // (apps/web/src/api/client.ts) reads this on 401 to silently
      // renew the 60d access token via POST /api/auth/refresh.
      if (data.refreshToken) localStorage.setItem('refreshToken', data.refreshToken)
      localStorage.setItem('userId', String(data.userId))
      localStorage.setItem('user', JSON.stringify(user))
      localStorage.setItem('language', user.preferredLanguage)
      // Clear the post-delete / post-block notice flag — next browser
      // session on this tab shouldn't see a stale notice from a prior user.
      try { sessionStorage.removeItem('jnati:accountReason') } catch {}
      setAuth(user, data.token)
      const from = (location.state as { from?: { pathname?: string } } | null)?.from?.pathname ?? '/'
      navigate(from, { replace: true })
    } catch (err) {
      setError(getErrorMessage(err, 'Login failed'))
    } finally {
      setLoading(false)
    }
  }

  return (
    <AuthHeroLayout
      subTagline="From close to extended relatives — Jnati helps you map, connect, and grow your relatives network."
      footer={
        <>
          New to Jnati?{' '}
          <Link to="/signup" className="text-gold font-bold hover:underline">Create an account</Link>
        </>
      }
      landingSections={
        <>
          <QuizCallout />
          <FeaturesSection />
          <HowItWorksSection />
          <TestimonialsSection />
          <AboutSection />
          <ContactSection />
        </>
      }
    >
      <form onSubmit={handleSubmit}>
        <h2 className="text-navy text-xl font-extrabold mb-4">Login to Jnati</h2>

        {/* Post-tombstone / post-block notice. Reason comes from
            sessionStorage (set by DeleteAccount on success + the
            apiClient interceptor on 403 ACCOUNT_DELETED/BLOCKED), with
            the legacy URL params as fallback. sessionStorage survives
            hard refresh and manual nav to /login; URL params don't.
            Cleared on successful login below. While a reason is showing
            we suppress the inline credentials error — tombstone wipes
            phone_number, so a retry reads as "Invalid credentials"
            (user-not-found shape); pairing that with the explicit
            notice is confusing. Notice wins. */}
        {(() => {
          const stored = (() => { try { return sessionStorage.getItem('jnati:accountReason') } catch { return null } })()
          const reason = stored === 'deleted' || new URLSearchParams(location.search).get('deleted') === '1'
            ? 'deleted'
            : stored === 'blocked' || new URLSearchParams(location.search).get('blocked') === '1'
            ? 'blocked'
            : null
          if (reason === 'deleted') {
            return (
              <div className="bg-danger/10 border border-danger rounded-lg p-3 mb-3 text-sm" role="status">
                <div className="font-bold mb-0.5 text-danger">Your account has been removed.</div>
                <p className="text-xs text-danger/80">You can no longer sign in. Contact an admin if this was a mistake.</p>
              </div>
            )
          }
          if (reason === 'blocked') {
            return (
              <div className="bg-danger/10 border border-danger rounded-lg p-3 mb-3 text-sm" role="status">
                <div className="font-bold mb-0.5 text-danger">Your account has been suspended.</div>
                <p className="text-xs text-danger/80">Contact an admin for more information.</p>
              </div>
            )
          }
          return error ? <p className="text-danger text-sm mb-3" role="alert">{error}</p> : null
        })()}

        <PhoneInput value={phone} onChange={setPhone} country={country} onCountryChange={setCountry} disabled={loading} />
        <PasswordInput value={password} onChange={setPassword} disabled={loading} />

        <div className="text-right mt-2">
          <Link to="/forgot-password" className="text-navy text-xs font-semibold hover:underline">
            Forgot password?
          </Link>
        </div>

        {/* Gold CTA per #7 — login button matches jnati.com marketing's
            login-card gold treatment. Other primary CTAs across the app
            stay navy; gold is specifically the auth-card focal-point
            pattern. */}
        <button
          type="submit"
          disabled={loading}
          className="w-full bg-gold text-navy rounded-lg py-3 mt-5 font-bold hover:brightness-95 disabled:opacity-60 disabled:cursor-not-allowed transition"
        >
          {loading ? 'Signing in…' : 'Login'}
        </button>
      </form>
    </AuthHeroLayout>
  )
}

// #kinship-quiz-cutover-route — login-page CTA for the standalone
// quiz at /quiz (V2). Card-style, gold-accented, sits in the
// landingSections column above the marketing surfaces. Telugu
// headline + English subtitle — the bilingual mix matches the
// brand pattern used elsewhere on auth surfaces.
function QuizCallout() {
  return (
    <section className="px-4 sm:px-6 py-8 sm:py-12">
      <div className="max-w-2xl mx-auto bg-navy/95 rounded-2xl border-2 border-gold/40 p-6 sm:p-8 text-center shadow-lg">
        <div className="text-gold text-xs sm:text-sm font-bold tracking-[0.25em] uppercase mb-2">
          JNATI · Kinship Challenge
        </div>
        <h3 className="text-2xl sm:text-3xl font-extrabold text-white mb-2">
          బంధుత్వాలు తెలుసా?
        </h3>
        <p className="text-white/80 text-sm sm:text-base mb-5 max-w-md mx-auto">
          Test your knowledge of Telugu kinship terms. Five levels, no
          sign-up required.
        </p>
        <Link
          to="/quiz"
          className="inline-block bg-gold text-navy font-bold px-6 py-3 rounded-lg hover:bg-gold/90 transition"
        >
          Play now →
        </Link>
      </div>
    </section>
  )
}
